import React, { useState, useEffect } from 'react';
import { BookOpen, CheckCircle, Clock, Edit, ArrowUp, Save } from 'lucide-react';
import AdminNavigation from '../../components/ui/AdminNavigation';
import { fatouRefinementService } from '../../services/fatouRefinementService';
import { supabase } from '../../lib/supabase';
import RefinementSuggestionsPanel from './components/RefinementSuggestionsPanel';
import ContentEditorPanel from './components/ContentEditorPanel';
import ApprovalWorkflowPanel from './components/ApprovalWorkflowPanel';
import ImpactAnalysisPanel from './components/ImpactAnalysisPanel';
import Icon from '../../components/AppIcon';


export default function FatouAIKnowledgeBaseRefinementCenter() {
  const [activeTab, setActiveTab] = useState('suggestions');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [refinements, setRefinements] = useState([]);
  const [fatouConfig, setFatouConfig] = useState(null);
  const [filters, setFilters] = useState({
    status: 'pending',
    priority: null,
    refinementType: null
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadRefinements();
  }, [filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [statsData, configData] = await Promise.all([
        fatouRefinementService?.getRefinementStats(),
        loadFatouConfig()
      ]);
      setStats(statsData);
      setFatouConfig(configData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFatouConfig = async () => {
    try {
      const { data, error } = await supabase?.from('fatou_config')?.select('*')?.eq('is_active', true)?.single();

      if (error) throw error;
      return data?.config_data || null;
    } catch (error) {
      console.error('Error loading Fatou config:', error);
      return null;
    }
  };

  const loadRefinements = async () => {
    try {
      const data = await fatouRefinementService?.getRefinements(filters);
      setRefinements(data);
    } catch (error) {
      console.error('Error loading refinements:', error);
    }
  };

  const handleCreateRefinement = async (refinementData) => {
    try {
      await fatouRefinementService?.createRefinement(refinementData);
      await loadRefinements();
      await loadData();
      alert('Suggestion créée avec succès!');
    } catch (error) {
      console.error('Error creating refinement:', error);
      alert('Erreur lors de la création de la suggestion');
    }
  };

  const handleUpdateStatus = async (refinementId, status, notes) => {
    try {
      await fatouRefinementService?.updateRefinementStatus(refinementId, status, notes);
      await loadRefinements();
      await loadData();
    } catch (error) {
      console.error('Error updating refinement status:', error);
      alert('Erreur lors de la mise à jour du statut');
    }
  };

  const handleApplyRefinement = async (refinementId) => {
    try {
      const confirm = window.confirm('Êtes-vous sûr de vouloir appliquer ce refinement à la configuration Fatou?');
      if (!confirm) return;

      await fatouRefinementService?.applyRefinement(refinementId);
      await loadRefinements();
      await loadData();
      alert('Refinement appliqué avec succès!');
    } catch (error) {
      console.error('Error applying refinement:', error);
      alert('Erreur lors de l\'application du refinement: ' + error?.message);
    }
  };

  const handleDeleteRefinement = async (refinementId) => {
    try {
      const confirm = window.confirm('Êtes-vous sûr de vouloir supprimer cette suggestion?');
      if (!confirm) return;

      await fatouRefinementService?.deleteRefinement(refinementId);
      await loadRefinements();
      await loadData();
    } catch (error) {
      console.error('Error deleting refinement:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const tabs = [
    { id: 'suggestions', label: 'Suggestions', icon: BookOpen, count: stats?.pending },
    { id: 'editor', label: 'Éditeur de Contenu', icon: Edit },
    { id: 'approval', label: 'Workflow d\'Approbation', icon: CheckCircle, count: stats?.approved },
    { id: 'impact', label: 'Analyse d\'Impact', icon: ArrowUp }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminNavigation />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mb-4"></div>
            <p className="text-gray-600">Chargement du centre de refinement...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Centre de Refinement Fatou IA</h1>
          <p className="text-gray-600">Gestion et amélioration continue de la base de connaissances</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.total || 0}</p>
              </div>
              <div className="p-3 bg-gray-100 rounded-lg">
                <BookOpen className="text-gray-600" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">En Attente</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.pending || 0}</p>
              </div>
              <div className="p-3 bg-yellow-100 rounded-lg">
                <Clock className="text-yellow-600" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Approuvés</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.approved || 0}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle className="text-green-600" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Implémentés</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.implemented || 0}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <Save className="text-blue-600" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Priorité Haute</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.highPriority || 0}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-lg">
                <ArrowUp className="text-red-600" size={24} />
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b border-gray-200">
            <div className="flex space-x-1 p-2 overflow-x-auto">
              {tabs?.map((tab) => {
                const Icon = tab?.icon;
                return (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center space-x-2 px-4 py-3 rounded-lg font-medium transition-colors whitespace-nowrap ${
                      activeTab === tab?.id
                        ? 'bg-green-600 text-white' :'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Icon size={18} />
                    <span>{tab?.label}</span>
                    {tab?.count !== undefined && tab?.count > 0 && (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                        activeTab === tab?.id ? 'bg-white text-green-600' : 'bg-gray-200 text-gray-700'
                      }`}>
                        {tab?.count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'suggestions' && (
              <RefinementSuggestionsPanel
                refinements={refinements}
                filters={filters}
                onFiltersChange={setFilters}
                onUpdateStatus={handleUpdateStatus}
                onDelete={handleDeleteRefinement}
                onRefresh={loadRefinements}
              />
            )}

            {activeTab === 'editor' && (
              <ContentEditorPanel
                fatouConfig={fatouConfig}
                onCreate={handleCreateRefinement}
                onRefresh={() => loadFatouConfig()?.then(setFatouConfig)}
              />
            )}

            {activeTab === 'approval' && (
              <ApprovalWorkflowPanel
                refinements={refinements?.filter(r => r?.status === 'approved')}
                onApply={handleApplyRefinement}
                onUpdateStatus={handleUpdateStatus}
                onRefresh={loadRefinements}
              />
            )}

            {activeTab === 'impact' && (
              <ImpactAnalysisPanel
                refinements={refinements}
                stats={stats}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}